import "./OrdersPage.css";
import "../components/Table.css";
import "../components/Print.css";
import { useContext, useMemo, useState } from "react";
import { OrderContext } from "../store/OrderContext";
import { useNavigate } from "react-router-dom";
import Searchbar from "../components/Searchbar";
import { getOrderYear, isOrderInYear, Order } from "../../../api/orders";
import ScrollableContainer from "../components/ScrollableContainer";
import LoadingSpinner from "../../LoadingSpinner";

const OrdersPage = () => {
  const { orders, loading, error } = useContext(OrderContext);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [filteredText, setFilteredText] = useState("");
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const navigate = useNavigate();

  const availableYears = useMemo(() => {
    const years = new Set<number>([currentYear]);
    for (const order of orders) {
      const year = getOrderYear(order.createdAt);
      if (year != null) years.add(year);
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [orders, currentYear]);

  const ordersForSelectedYear = useMemo(
    () =>
      orders.filter((order) => isOrderInYear(order.createdAt, selectedYear)),
    [orders, selectedYear],
  );

  const ordersToDisplay = filteredText ? filteredOrders : ordersForSelectedYear;

  const statusRank = (status: string) => {
    if (status === "Pending") return 0;
    if (status === "Done") return 1;
    return 2; // Canceled (and any other status) at the bottom
  };

  const sortedOrders = [...ordersToDisplay].sort((a, b) => {
    const byStatus = statusRank(a.status) - statusRank(b.status);
    if (byStatus !== 0) return byStatus;

    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  const orderList = sortedOrders.map((order) => {
    const formattedDate = new Date(order.createdAt).toLocaleDateString();
    const status =
      order.status === "Done"
        ? "Finished"
        : order.status === "Canceled"
          ? "Canceled"
          : "Pending";

    return (
      <tr
        key={order.id}
        className="order-clickable-table-row"
        onClick={() => goToOrderDetails(order.id)}
      >
        <td className="td">{order.businessName}</td>
        <td className="td">{formattedDate}</td>
        <td className="td">
          <span
            className={
              order.status === "Done"
                ? "order-done"
                : order.status === "Canceled"
                  ? "order-canceled"
                  : ""
            }
          >
            {status}
          </span>
        </td>
        <td className="td capitalized">
          {order.deliveryMethod === "pickup" ? "Pickup" : "Shipping"}
        </td>
        <td className="td ">{order.id}</td>
      </tr>
    );
  });

  function goToOrderDetails(id: string) {
    navigate(id);
  }

  function filterOrders(value: string) {
    const filteredOrders = ordersForSelectedYear.filter(
      (order) =>
        order.businessName.toLowerCase().includes(value.toLowerCase()) ||
        order.id.toLowerCase().includes(value.toLowerCase()),
    );
    setFilteredOrders(filteredOrders);
    setFilteredText(value);
  }

  function handleYearChange(event: React.ChangeEvent<HTMLSelectElement>) {
    setSelectedYear(Number(event.target.value));
    setFilteredText("");
    setFilteredOrders([]);
  }

  return (
    <div className="page-container">
      <h1>Orders</h1>
      <div className="page-content orders-page-content">
        {error && (
          <p className="error-message" role="alert">
            {error}
          </p>
        )}
        <div className="orders-page-header">
          <Searchbar
            action={filterOrders}
            placeholder="Search for order or restaurant..."
          />
          <div className="orders-page-year-filter">
            <label htmlFor="orders-year-select" className="sr-only">
              Year
            </label>
            <select
              id="orders-year-select"
              className="dropdown orders-year-select"
              value={selectedYear}
              onChange={handleYearChange}
              disabled={loading}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="order-table-container">
            <ScrollableContainer>
              <table className="table">
                <thead className="sticky-table-head">
                  <tr>
                    <th className="th">Restaurant</th>
                    <th className="th">Date</th>
                    <th className="th">Status</th>
                    <th className="th">Delivery Method</th>
                    <th className="th">Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredText && filteredOrders.length === 0 ? (
                    <p className="not-found-message">
                      There are no orders that match your filter.
                    </p>
                  ) : !filteredText && ordersForSelectedYear.length === 0 ? (
                    <tr>
                      <td colSpan={5}>
                        <p className="not-found-message">
                          There are no orders from {selectedYear}.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    orderList
                  )}
                </tbody>
              </table>
            </ScrollableContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
